import { useState, useCallback } from 'react'
import { apiClient } from '../../lib/api'

// Type definitions
interface ApiParams {
  [key: string]: unknown
}

interface NoticeFilters extends ApiParams {
  cpv_codes?: string[]
  organization_name?: string
  contractor_name?: string
  date_from?: string
  date_to?: string
  notice_types?: string[]
  limit?: number
  offset?: number
}

interface PresetData {
  name: string
  filters: NoticeFilters
  description?: string
}

interface StatisticsParams {
  territory_id?: string
  date_from?: string
  date_to?: string
  level?: string
  limit?: number
  cpv_code?: string
  organization_id?: string
  contractor_name?: string
  organization_name?: string
  year?: string | number
  period?: string
}

interface PredictionCallbacks {
  onStep?: (data: { step: string; content: string }) => void
  onResult?: (data: unknown) => void
  onError?: (error: string) => void
}

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token')
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

export function useApi() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const apiCall = useCallback(
    async (method: string, url: string, data: unknown = null): Promise<any> => {
      setLoading(true)
      setError(null)

      try {
        const response = await apiClient({
          method,
          url,
          data,
        })
        return response.data
      } catch (err: any) {
        const errorMessage = err.response?.data?.detail || err.message || 'Wystąpił błąd'
        setError(errorMessage)
        throw err
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  const getStats = useCallback(() => apiCall('GET', '/stats'), [apiCall])

  const getNotices = useCallback(
    (params: NoticeFilters = {}) => {
      const queryParams = new URLSearchParams()
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            // Handle arrays by adding multiple query parameters with the same key
            // Filter out empty strings from arrays
            value
              .filter((item) => item !== undefined && item !== null && item !== '')
              .forEach((item) => {
                queryParams.append(key, item)
              })
          } else {
            queryParams.append(key, value as string)
          }
        }
      })
      const queryString = queryParams.toString()
      const url = `/notices${queryString ? `?${queryString}` : ''}`
      return apiCall('GET', url)
    },
    [apiCall],
  )

  const getNoticeDetail = useCallback(
    (noticeId: string) => apiCall('GET', `/notices/${noticeId}`),
    [apiCall],
  )

  const getRelatedNotices = useCallback(
    (noticeId: string) => apiCall('GET', `/notices/${noticeId}/related`),
    [apiCall],
  )

  const getSyncHistory = useCallback(
    (limit = 20) => apiCall('GET', `/sync-history?limit=${limit}`),
    [apiCall],
  )

  const getJobs = useCallback(() => apiCall('GET', '/jobs'), [apiCall])

  // Open Tenders functions
  const getOpenTenders = useCallback(
    (params: any = {}) => {
      const queryParams = new URLSearchParams()
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            // Handle arrays by adding multiple query parameters with the same key
            // Filter out empty strings from arrays
            value
              .filter((item) => item !== undefined && item !== null && item !== '')
              .forEach((item) => {
                queryParams.append(key, item)
              })
          } else {
            queryParams.append(key, value as string)
          }
        }
      })
      const queryString = queryParams.toString()
      const url = `/open-tenders/${queryString ? `?${queryString}` : ''}`
      return apiCall('GET', url)
    },
    [apiCall],
  )

  const getOpenTendersStats = useCallback(
    (params: any = {}) => {
      const queryParams = new URLSearchParams()
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            // Handle arrays by adding multiple query parameters with the same key
            value
              .filter((item) => item !== undefined && item !== null && item !== '')
              .forEach((item) => {
                queryParams.append(key, item)
              })
          } else {
            queryParams.append(key, value as string)
          }
        }
      })
      const queryString = queryParams.toString()
      const url = `/open-tenders/stats${queryString ? `?${queryString}` : ''}`
      return apiCall('GET', url)
    },
    [apiCall],
  )

  // CPV API functions
  const getCPVHierarchy = useCallback(() => apiCall('GET', '/cpv/hierarchy'), [apiCall])

  const searchCPVCodes = useCallback(
    (query: string, limit = 50) => {
      const params = new URLSearchParams({ q: query, limit: limit.toString() })
      return apiCall('GET', `/cpv/search?${params}`)
    },
    [apiCall],
  )

  const getCPVPath = useCallback((code: string) => apiCall('GET', `/cpv/path/${code}`), [apiCall])

  const getCPVChildren = useCallback(
    (code: string) => apiCall('GET', `/cpv/children/${code}`),
    [apiCall],
  )

  // Preset functions
  const getPresets = useCallback(() => apiCall('GET', '/presets'), [apiCall])

  const createPreset = useCallback(
    (presetData: PresetData) => apiCall('POST', '/presets', presetData),
    [apiCall],
  )

  const getPresetDetail = useCallback(
    (presetId: string) => apiCall('GET', `/presets/${presetId}`),
    [apiCall],
  )

  const updatePreset = useCallback(
    (presetId: string, presetData: PresetData) => apiCall('PUT', `/presets/${presetId}`, presetData),
    [apiCall],
  )

  const deletePreset = useCallback(
    (presetId: string) => apiCall('DELETE', `/presets/${presetId}`),
    [apiCall],
  )

  const usePreset = useCallback(
    (presetId: string) => apiCall('POST', `/presets/${presetId}/use`),
    [apiCall],
  )

  // Autocomplete functions
  const getOrganizationSuggestions = useCallback(
    (query: any, limit = 20) => {
      const params = new URLSearchParams({ q: query, limit: limit.toString() })
      return apiCall('GET', `/organizations/suggestions?${params}`)
    },
    [apiCall],
  )

  const getContractorSuggestions = useCallback(
    (query: any, limit = 20) => {
      const params = new URLSearchParams({ q: query, limit: limit.toString() })
      return apiCall('GET', `/contractors/suggestions?${params}`)
    },
    [apiCall],
  )

  const getCPVSuggestions = useCallback(
    (query: any, limit = 20) => {
      const params = new URLSearchParams({ q: query, limit: limit.toString() })
      return apiCall('GET', `/cpv/suggestions?${params}`)
    },
    [apiCall],
  )

  // Statistics functions
  const getStatisticsOverview = useCallback(
    (territoryId: any, dateFrom: any, dateTo: any) => {
      const params = new URLSearchParams()
      if (territoryId) params.append('territory_id', territoryId)
      if (dateFrom) params.append('date_from', dateFrom)
      if (dateTo) params.append('date_to', dateTo)
      const queryString = params.toString()
      return apiCall('GET', `/statistics/overview${queryString ? `?${queryString}` : ''}`)
    },
    [apiCall],
  )

  const getCPVBreakdown = useCallback(
    (territoryId: any, dateFrom: any, dateTo: any, level = 'division') => {
      const params = new URLSearchParams()
      if (territoryId) params.append('territory_id', territoryId)
      if (dateFrom) params.append('date_from', dateFrom)
      if (dateTo) params.append('date_to', dateTo)
      params.append('level', level)
      const queryString = params.toString()
      return apiCall('GET', `/statistics/cpv-breakdown?${queryString}`)
    },
    [apiCall],
  )

  const getOrganizations = useCallback(
    (territoryId: any, cpvCode: any, limit = 20, dateFrom?: any, dateTo?: any) => {
      const params = new URLSearchParams()
      if (territoryId) params.append('territory_id', territoryId)
      if (cpvCode) params.append('cpv_code', cpvCode)
      params.append('limit', limit.toString())
      if (dateFrom) params.append('date_from', dateFrom)
      if (dateTo) params.append('date_to', dateTo)
      const queryString = params.toString()
      return apiCall('GET', `/statistics/organizations?${queryString}`)
    },
    [apiCall],
  )

  const getContractors = useCallback(
    (
      territoryId: any,
      cpvCode: any,
      organizationId: any,
      limit = 20,
      dateFrom?: any,
      dateTo?: any,
    ) => {
      const params = new URLSearchParams()
      if (territoryId) params.append('territory_id', territoryId)
      if (cpvCode) params.append('cpv_code', cpvCode)
      if (organizationId) params.append('organization_id', organizationId)
      params.append('limit', limit.toString())
      if (dateFrom) params.append('date_from', dateFrom)
      if (dateTo) params.append('date_to', dateTo)
      const queryString = params.toString()
      return apiCall('GET', `/statistics/contractors?${queryString}`)
    },
    [apiCall],
  )

  // New Statistics API functions for redesigned Statistics page
  const getContractNotices = useCallback(
    (
      territoryId: any,
      organizationId: any,
      cpvCode: any,
      dateFrom: any,
      dateTo: any,
      period = 'yearly',
    ) => {
      const params = new URLSearchParams()
      if (territoryId) params.append('territory_id', territoryId)
      if (organizationId) params.append('organization_id', organizationId)
      if (cpvCode) params.append('cpv_code', cpvCode)
      if (dateFrom) params.append('date_from', dateFrom)
      if (dateTo) params.append('date_to', dateTo)
      params.append('period', period)
      const queryString = params.toString()
      return apiCall('GET', `/statistics/contract-notices?${queryString}`)
    },
    [apiCall],
  )

  const getTenderResults = useCallback(
    (
      territoryId: any,
      organizationId: any,
      contractorId: any,
      cpvCode: any,
      dateFrom: any,
      dateTo: any,
      period = 'yearly',
    ) => {
      const params = new URLSearchParams()
      if (territoryId) params.append('territory_id', territoryId)
      if (organizationId) params.append('organization_id', organizationId)
      if (contractorId) params.append('contractor_name', contractorId) // API expects contractor_name
      if (cpvCode) params.append('cpv_code', cpvCode)
      if (dateFrom) params.append('date_from', dateFrom)
      if (dateTo) params.append('date_to', dateTo)
      params.append('period', period)
      const queryString = params.toString()
      return apiCall('GET', `/statistics/tender-results?${queryString}`)
    },
    [apiCall],
  )

  const getOrganizationContractors = useCallback(
    (organizationName: any, cpvCode: any, dateFrom: any, dateTo: any, year: any, limit = 20) => {
      const params = new URLSearchParams()
      params.append('organization_name', organizationName)
      if (cpvCode) params.append('cpv_code', cpvCode)
      if (dateFrom) params.append('date_from', dateFrom)
      if (dateTo) params.append('date_to', dateTo)
      if (year) params.append('year', year.toString())
      params.append('limit', limit.toString())
      const queryString = params.toString()
      return apiCall('GET', `/statistics/dw/organization-contractors?${queryString}`)
    },
    [apiCall],
  )

  const getContractorOrganizations = useCallback(
    (contractorName: any, cpvCode: any, dateFrom: any, dateTo: any, year: any, limit = 20) => {
      const params = new URLSearchParams()
      params.append('contractor_name', contractorName)
      if (cpvCode) params.append('cpv_code', cpvCode)
      if (dateFrom) params.append('date_from', dateFrom)
      if (dateTo) params.append('date_to', dateTo)
      if (year) params.append('year', year)
      params.append('limit', limit.toString())
      const queryString = params.toString()
      return apiCall('GET', `/statistics/dw/contractor-organizations?${queryString}`)
    },
    [apiCall],
  )

  const getContractorOrganizationsByUuid = useCallback(
    (contractorUuid: any, cpvCode: any, dateFrom: any, dateTo: any, year: any, limit = 20) => {
      const params = new URLSearchParams()
      if (cpvCode) params.append('cpv_code', cpvCode)
      if (dateFrom) params.append('date_from', dateFrom)
      if (dateTo) params.append('date_to', dateTo)
      if (year) params.append('year', year)
      params.append('limit', limit.toString())
      const queryString = params.toString()
      return apiCall(
        'GET',
        `/statistics/contractor/${contractorUuid}/organizations${queryString ? `?${queryString}` : ''}`,
      )
    },
    [apiCall],
  )

  const getOrganizationContractorContracts = useCallback(
    (
      organizationName: string,
      contractorUuid: string,
      year?: number | string,
      dateFrom?: string,
      dateTo?: string,
    ) => {
      const params = new URLSearchParams()
      params.append('organization_name', organizationName)
      params.append('contractor_uuid', contractorUuid)
      if (year) params.append('year', year.toString())
      if (dateFrom) params.append('date_from', dateFrom)
      if (dateTo) params.append('date_to', dateTo)
      const queryString = params.toString()
      return apiCall('GET', `/statistics/dw/organization-contractor-contracts?${queryString}`)
    },
    [apiCall],
  )

  // Data Warehouse Statistics functions
  const refreshStatistics = useCallback(() => apiCall('POST', '/statistics/dw/refresh'), [apiCall])

  const getRefreshStatus = useCallback(() => apiCall('GET', '/statistics/dw/status'), [apiCall])

  const getTopContractors = useCallback(
    (year: any, limit = 20, cpvCode?: any, contractorName?: any) => {
      const params = new URLSearchParams()
      if (year) params.append('year', year)
      if (cpvCode) params.append('cpv_code', cpvCode)
      if (contractorName) params.append('contractor_name', contractorName)
      params.append('limit', limit.toString())
      const queryString = params.toString()
      return apiCall('GET', `/statistics/dw/contractors/top?${queryString}`)
    },
    [apiCall],
  )

  const getTopOrganizations = useCallback(
    (year: any, limit = 20, cpvCode?: any, organizationName?: any) => {
      const params = new URLSearchParams()
      if (year) params.append('year', year)
      if (cpvCode) params.append('cpv_code', cpvCode)
      if (organizationName) params.append('organization_name', organizationName)
      params.append('limit', limit.toString())
      const queryString = params.toString()
      return apiCall('GET', `/statistics/dw/organizations/top?${queryString}`)
    },
    [apiCall],
  )

  const getTopCPV = useCallback(
    (year: any, level = 2, limit = 20) => {
      const params = new URLSearchParams()
      if (year) params.append('year', year)
      params.append('level', level.toString())
      params.append('limit', limit.toString())
      const queryString = params.toString()
      return apiCall('GET', `/statistics/dw/cpv/top?${queryString}`)
    },
    [apiCall],
  )

  // Dictionary functions
  const getDictionaries = useCallback(() => apiCall('GET', '/dictionaries'), [apiCall])

  const getDictionaryItems = useCallback(
    (name: any) => apiCall('GET', `/dictionaries/${name}`),
    [apiCall],
  )

  // System functions
  const resetDatabase = useCallback(
    (partial = false) => apiCall('POST', `/system/reset?partial=${partial}`),
    [apiCall],
  )

  // Prediction functions
  const generatePrediction = useCallback(
    (
      noticeId: string,
      onStep?: (data: any) => void,
      onResult?: (data: any) => void,
      onError?: (error: string) => void,
    ) => {
      const controller = new AbortController()

      // Start prediction job
      fetch(`${apiClient.defaults.baseURL}/notices/${noticeId}/prediction`, {
        method: 'POST',
        headers: getAuthHeaders(),
        signal: controller.signal,
      })
        .then(async (response) => {
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}))
            throw new Error(errorData.detail || `HTTP ${response.status}`)
          }

          const jobData = await response.json()
          const jobId = jobData.job_id

          // Poll for job status
          const pollInterval = setInterval(async () => {
            try {
              const jobResponse = await fetch(`${apiClient.defaults.baseURL}/prediction-jobs/${jobId}`, {
                signal: controller.signal,
                headers: getAuthHeaders(),
              })

              if (!jobResponse.ok) {
                throw new Error(`HTTP ${jobResponse.status}`)
              }

              const job = await jobResponse.json()

              // Report progress steps
              if (job.current_step && job.current_step_detail && onStep) {
                onStep({
                  step: job.current_step,
                  content: job.current_step_detail,
                })
              }

              // Check job status
              if (job.status === 'completed' && onResult) {
                // Job completed, get final prediction
                clearInterval(pollInterval)

                try {
                  const predictionResponse = await fetch(
                    `${apiClient.defaults.baseURL}/notices/${noticeId}/prediction`,
                    {
                      signal: controller.signal,
                      headers: getAuthHeaders(),
                    },
                  )

                  if (!predictionResponse.ok) {
                    throw new Error(`Failed to get prediction: HTTP ${predictionResponse.status}`)
                  }

                  const prediction = await predictionResponse.json()
                  onResult(prediction)
                } catch (predictionErr: any) {
                  if (onError) {
                    onError(`Job completed but failed to get prediction: ${predictionErr.message}`)
                  }
                }
              } else if (job.status === 'failed') {
                // Job failed
                clearInterval(pollInterval)
                if (onError) {
                  onError(job.error_message || 'Prediction job failed')
                }
              } else if (job.status === 'cancelled') {
                // Job was cancelled
                clearInterval(pollInterval)
                if (onError) {
                  onError('Prediction job was cancelled')
                }
              }
              // Continue polling for 'running' or 'created' status
            } catch (pollErr: any) {
              if (pollErr.name !== 'AbortError') {
                clearInterval(pollInterval)
                if (onError) {
                  onError(`Failed to poll job status: ${pollErr.message}`)
                }
              }
            }
          }, 2000) // Poll every 2 seconds

          // Clean up interval on abort
          controller.signal.addEventListener('abort', () => {
            clearInterval(pollInterval)
          })
        })
        .catch((err: any) => {
          if (err.name !== 'AbortError' && onError) {
            onError(err.message || 'Błąd podczas uruchamiania predykcji')
          }
        })

      return controller
    },
    [],
  )

  const getPrediction = useCallback(
    (noticeId: string) => apiCall('GET', `/notices/${noticeId}/prediction`),
    [apiCall],
  )

  return {
    loading,
    error,
    apiCall,
    getStats,
    getNotices,
    getNoticeDetail,
    getRelatedNotices,
    getSyncHistory,
    getJobs,
    // Open Tenders functions
    getOpenTenders,
    getOpenTendersStats,
    // CPV functions
    getCPVHierarchy,
    searchCPVCodes,
    getCPVPath,
    getCPVChildren,
    // Autocomplete functions
    getOrganizationSuggestions,
    getContractorSuggestions,
    getCPVSuggestions,
    // Preset functions
    getPresets,
    createPreset,
    getPresetDetail,
    updatePreset,
    deletePreset,
    usePreset,
    // Statistics functions
    getStatisticsOverview,
    getCPVBreakdown,
    getOrganizations,
    getContractors,
    // New Statistics functions for redesigned Statistics page
    getContractNotices,
    getTenderResults,
    getOrganizationContractors,
    getContractorOrganizations,
    getContractorOrganizationsByUuid,
    getOrganizationContractorContracts,
    // Data Warehouse Statistics functions
    refreshStatistics,
    getRefreshStatus,
    getTopContractors,
    getTopOrganizations,
    getTopCPV,
    // Dictionary functions
    getDictionaries,
    getDictionaryItems,
    // System functions
    resetDatabase,
    // Prediction functions
    generatePrediction,
    getPrediction,
  }
}
